/**
 * Renders an agent-drafted email so the rep can preview the formatted body
 * and copy each piece into Gmail with one click. No backend send — this is
 * the v1 path; the OAuth-based send-on-behalf is a follow-up.
 *
 * Layout is mail-client-shaped: To/Subject as compact horizontal rows, the
 * preview takes the bulk of the height, secondary detail (plaintext +
 * rationale) is collapsed by default, and the primary "Copy formatted body"
 * CTA lives in the dialog footer as the action the rep is most likely to
 * end on.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Button,
  Collapse,
  Link,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { copyHtmlToClipboard, copyTextToClipboard } from '../utils/clipboard';
import type { EmailDraft } from '../utils/parseEmailDraft';

interface Props {
  open: boolean;
  draft: EmailDraft | null;
  onClose: () => void;
}

type CopyTarget = 'to' | 'subject' | 'body' | 'plain' | null;

const COPY_FEEDBACK_MS = 1500;
const LABEL_WIDTH = 64;

function looksLikeEmail(value: string): boolean {
  // Permissive — reps can paste corrected addresses in Gmail anyway. We just
  // want to flag obvious misses like "(unknown)" or empty strings.
  return /\S+@\S+\.\S+/.test(value);
}

export default function EmailComposerModal({ open, draft, onClose }: Props) {
  const [copiedTarget, setCopiedTarget] = useState<CopyTarget>(null);
  const [showPlainText, setShowPlainText] = useState(false);
  const [showRationale, setShowRationale] = useState(false);
  // Surfaced when both the Clipboard API and the execCommand fallback fail
  // (Safari with permissions revoked, sandboxed iframe, ancient browser).
  // Without this the rep clicks "Copy" → nothing happens → they click again.
  const [copyFailureMessage, setCopyFailureMessage] = useState<string | null>(null);

  // Reset transient UI state when the modal closes (or the draft swaps under it).
  useEffect(() => {
    if (!open) {
      setCopiedTarget(null);
      setShowPlainText(false);
      setShowRationale(false);
      setCopyFailureMessage(null);
    }
  }, [open, draft]);

  const previewSrcDoc = useMemo(() => {
    if (!draft) return '';
    // Wrap the agent's HTML so the iframe renders it the way Gmail will:
    // explicit doctype, light background, system font fallback. Note: any
    // `<a>` opens in a new tab via <base target="_blank">.
    return `<!doctype html><html><head><meta charset="utf-8"><base target="_blank">
<style>
  body { margin: 0; padding: 16px; background: #ffffff; color: #1a1a1a;
         font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
         font-size: 14px; line-height: 1.5; }
  a { color: #1a73e8; }
</style></head><body>${draft.html}</body></html>`;
  }, [draft]);

  // Gmail's compose URL contract: `body` is plain text only. The rep gets
  // To/Subject prefilled and pastes the rich body via Cmd-V — the modal's
  // primary action remains the rich-text copy.
  const gmailComposeUrl = useMemo(() => {
    if (!draft) return '';
    const params = new URLSearchParams({
      view: 'cm',
      fs: '1',
      to: looksLikeEmail(draft.to) ? draft.to : '',
      su: draft.subject,
      body: draft.plainText,
    });
    return `https://mail.google.com/mail/?${params.toString()}`;
  }, [draft]);

  if (!draft) return null;

  const handleCopy = async (target: CopyTarget, value: string, asHtml = false) => {
    const ok = asHtml ? await copyHtmlToClipboard(value) : await copyTextToClipboard(value);
    if (ok) {
      setCopiedTarget(target);
      window.setTimeout(() => {
        setCopiedTarget((current) => (current === target ? null : current));
      }, COPY_FEEDBACK_MS);
    } else {
      // Both Clipboard API and execCommand fallback failed. Tell the rep so
      // they don't click in vain — the plain-text panel below has selectable
      // text that always works.
      setCopyFailureMessage(
        asHtml
          ? "Couldn't copy automatically — open the 'Plain-text fallback' panel below and copy from there."
          : "Couldn't copy automatically — please select and copy manually."
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: 'min(720px, 90vh)' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1.25,
          pr: 1.5,
          pl: 2,
        }}
      >
        <Typography variant="subtitle1" component="span" sx={{ flex: 1, fontWeight: 600 }}>
          Email composer
        </Typography>
        <Tooltip title="Open a new Gmail compose tab with To and Subject prefilled" arrow>
          <Button
            size="small"
            variant="text"
            startIcon={<OpenInNewIcon fontSize="small" />}
            component={Link}
            href={gmailComposeUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textTransform: 'none' }}
          >
            Open in Gmail
          </Button>
        </Tooltip>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {/* Compact header rows, one line each */}
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <FieldRow
            label="To"
            value={draft.to}
            warning={!looksLikeEmail(draft.to)}
            warningHint="Not a valid email — fill in manually before sending."
            copied={copiedTarget === 'to'}
            onCopy={() => handleCopy('to', draft.to)}
          />
          <FieldRow
            label="Subject"
            value={draft.subject}
            copied={copiedTarget === 'subject'}
            onCopy={() => handleCopy('subject', draft.subject)}
            sx={{ mt: 0.25 }}
          />
        </Box>

        {/* Preview takes the rest of the available height */}
        <Box sx={{ flex: 1, minHeight: 0, p: 2, overflow: 'hidden' }}>
          <Box
            component="iframe"
            title="Email preview"
            srcDoc={previewSrcDoc}
            sandbox="allow-same-origin"
            sx={{
              width: '100%',
              height: '100%',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.default',
              display: 'block',
            }}
          />
        </Box>

        {/* Secondary panels, collapsed by default */}
        <Box sx={{ borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
          <DisclosureRow
            label="Plain-text fallback"
            open={showPlainText}
            onToggle={() => setShowPlainText((s) => !s)}
          />
          <Collapse in={showPlainText}>
            <Box sx={{ px: 2, pb: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  multiline
                  fullWidth
                  minRows={3}
                  maxRows={6}
                  value={draft.plainText}
                  InputProps={{ readOnly: true, sx: { fontSize: 13 } }}
                  variant="outlined"
                  size="small"
                />
                <Tooltip title={copiedTarget === 'plain' ? 'Copied' : 'Copy plain text'} arrow>
                  <IconButton
                    size="small"
                    onClick={() => handleCopy('plain', draft.plainText)}
                    aria-label="Copy plain text"
                  >
                    {copiedTarget === 'plain' ? (
                      <CheckIcon fontSize="small" color="success" />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          </Collapse>

          {draft.rationale && (
            <>
              <Box sx={{ borderTop: 1, borderColor: 'divider' }} />
              <DisclosureRow
                label="Why this draft (agent's rationale)"
                open={showRationale}
                onToggle={() => setShowRationale((s) => !s)}
              />
              <Collapse in={showRationale}>
                <Box
                  sx={{
                    px: 2,
                    pb: 1.5,
                    color: 'text.secondary',
                    fontSize: 13,
                    // Trim markdown's default vertical rhythm — the rationale is
                    // a tight bullet list inside a collapsible, not a doc page.
                    '& p': { my: 0.5 },
                    '& ul, & ol': { pl: 2.5, my: 0.5 },
                    '& li': { mb: 0.25 },
                    '& li > p': { my: 0 },
                    '& strong': { color: 'text.primary', fontWeight: 600 },
                    '& code': {
                      bgcolor: 'action.hover',
                      px: 0.5,
                      borderRadius: 0.5,
                      fontFamily: 'monospace',
                      fontSize: '0.85em',
                    },
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {draft.rationale}
                  </ReactMarkdown>
                </Box>
              </Collapse>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.25 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          Copies as rich text — pasting into Gmail preserves formatting.
        </Typography>
        <Button onClick={onClose} size="small" sx={{ textTransform: 'none' }}>
          Close
        </Button>
        <Button
          variant="contained"
          color={copiedTarget === 'body' ? 'success' : 'primary'}
          startIcon={
            copiedTarget === 'body' ? (
              <CheckIcon fontSize="small" />
            ) : (
              <ContentCopyIcon fontSize="small" />
            )
          }
          onClick={() => handleCopy('body', draft.html, true)}
          sx={{ textTransform: 'none', minWidth: 180 }}
        >
          {copiedTarget === 'body' ? 'Copied' : 'Copy formatted body'}
        </Button>
      </DialogActions>

      <Snackbar
        open={!!copyFailureMessage}
        autoHideDuration={6000}
        onClose={() => setCopyFailureMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          onClose={() => setCopyFailureMessage(null)}
          sx={{ width: '100%' }}
        >
          {copyFailureMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

interface FieldRowProps {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
  warning?: boolean;
  warningHint?: string;
  sx?: object;
}

function FieldRow({ label, value, copied, onCopy, warning, warningHint, sx }: FieldRowProps) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ minHeight: 32, ...(sx || {}) }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          width: LABEL_WIDTH,
          flexShrink: 0,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          color={warning ? 'warning.main' : 'text.primary'}
          sx={{
            fontFamily: warning ? 'monospace' : undefined,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={value}
        >
          {value}
        </Typography>
      </Box>
      {warning && warningHint && (
        <Tooltip title={warningHint} arrow>
          <WarningAmberIcon fontSize="small" sx={{ color: 'warning.main' }} />
        </Tooltip>
      )}
      <Tooltip title={copied ? 'Copied' : `Copy ${label.toLowerCase()}`} arrow>
        <IconButton size="small" onClick={onCopy} aria-label={`Copy ${label}`}>
          {copied ? (
            <CheckIcon fontSize="small" color="success" />
          ) : (
            <ContentCopyIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

interface DisclosureRowProps {
  label: string;
  open: boolean;
  onToggle: () => void;
}

function DisclosureRow({ label, open, onToggle }: DisclosureRowProps) {
  return (
    <Box
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      sx={{
        px: 2,
        py: 0.75,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <KeyboardArrowRightIcon
        fontSize="small"
        sx={{
          transition: 'transform 0.15s',
          transform: open ? 'rotate(90deg)' : 'none',
          color: 'text.secondary',
        }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
  );
}
